import { useQuery } from '@apollo/client';
import { translateBackend } from '@assets/i18n/config';
import { getSubAccountName } from '@interfaces/subAccount';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@ovComponents/2-component/accordion/accordion';

import { useConfirmationDialog } from '@ovComponents/4-module/workflowCompletion/components/useConfirmationDialog';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { useLocalization } from '@utils/useLocalization';
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from 'hephaestus/web';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
} from 'hephaestus/webIcons';
import { type ComponentProps, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FETCH_SUBACCOUNT_DETAILS } from './depositReview.queries';
import type { DepositReviewSubstepOptions } from './depositReview.visual';

const FieldDisplay = ({
  title,
  children,
  expanded = true,
}: {
  title: string;
  children: ReactNode;
  expanded?: boolean;
}) => (
  <Grid
    container
    size={12}
    sx={{
      columnGap: 2,
    }}
  >
    <Grid size={expanded ? 12 : 3}>
      <Typography variant='bodyMedium' sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
    </Grid>
    <Grid size={expanded ? 12 : 7}>
      <Typography variant='bodyMedium' sx={{ fontWeight: 400 }}>
        {children}
      </Typography>
    </Grid>
  </Grid>
);

export const DisclaimerBox = ({ text }: { text: string }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 1,
        background: theme.color.surfaceContainerVariant,
        borderRadius: theme.borderRadius.md,
      }}
    >
      <Typography variant='bodySmall' component='div' sx={{ mx: 1, my: -1 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {text}
        </ReactMarkdown>
      </Typography>
    </Box>
  );
};

type LegacyAccountTsType = Parameters<typeof getSubAccountName>[0];

export const DepositCard = ({
  deposit,
  onDepositDeleted,
  onDepositEdit,
  enableDelete = true,
  enableEdit = true,
  options,
}: {
  deposit: TransferContextData;
  index: number;
  onDepositDeleted: (temporaryId: string) => void;
  onDepositEdit: (temporaryId: string) => void;
  enableDelete?: boolean;
  enableEdit?: boolean;
  options: DepositReviewSubstepOptions;
}) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation(['workflowCompletions', 'accountTypes']);
  const { localizedDate } = useLocalization();
  const { confirmationDialog, getUserConfirmation } = useConfirmationDialog();

  // Fetch detailed subaccount information for proper display
  const { data: subAccountData } = useQuery(FETCH_SUBACCOUNT_DETAILS, {
    variables: { subAccountId: deposit.subAccount?.id as string },
    skip: !deposit.subAccount?.id,
  });

  const { data: fromSubAccountData } = useQuery(FETCH_SUBACCOUNT_DETAILS, {
    variables: { subAccountId: deposit.fromSubAccount?.id as string },
    skip: !deposit.fromSubAccount?.id,
  });

  // Use detailed data if available, fallback to partial data
  const detailedSubAccount =
    subAccountData?.fetchSubAccount?.subAccount || deposit.subAccount;
  const detailedFromSubAccount =
    fromSubAccountData?.fetchSubAccount?.subAccount || deposit.fromSubAccount;

  const formatAmount = (amountCents?: number) => {
    return formatMoneyValue(amountCents, '$', 2, false);
  };

  const fullTransfer = () =>
    detailedFromSubAccount?.statistics?.marketValueCents &&
    (deposit.amountCents || 0) >
      (detailedFromSubAccount?.statistics?.marketValueCents || 0) * 0.97;

  const getDepositTypeLabel = (type?: string) => {
    switch (type) {
      case 'EFT':
        return t('depositTypeOptions.EFT');
      case 'INTERNAL_TRANSFER':
        return t('depositTypeOptions.INTERNAL_TRANSFER');
      case 'EXTERNAL_TRANSFER':
        return t('depositTypeOptions.EXTERNAL_TRANSFER');
      default:
        return type || t('depositReview.unknownDepositType');
    }
  };

  const getDepositSourceLabel = () => {
    switch (deposit.type) {
      case 'EFT':
        return t('depositReview.fromBankAccount');
      case 'INTERNAL_TRANSFER':
        return t('depositReview.fromSubAccount');
      case 'EXTERNAL_TRANSFER':
        return t('depositReview.financialInstitution');
      default:
        return 'From';
    }
  };

  const getDepositSource = () => {
    switch (deposit.type) {
      case 'EFT':
        if (
          deposit.bankAccount?.name &&
          deposit.bankAccount?.bankAccountNumber
        ) {
          const last4 = deposit.bankAccount.bankAccountNumber.slice(-4);
          return `${deposit.bankAccount.name} ***${last4}`;
        }
        return 'Bank Account';
      case 'INTERNAL_TRANSFER': {
        const fromSubAccountName = getSubAccountName(
          detailedFromSubAccount as LegacyAccountTsType,
        );
        return fromSubAccountName;
      }
      case 'EXTERNAL_TRANSFER':
        if (deposit.institution?.name && deposit.accountNumber) {
          const last4 = deposit.accountNumber.slice(-4);
          return `${deposit.institution.name} ***${last4}`;
        }
        return 'External Account';
      default:
        return 'Unknown Source';
    }
  };

  const disclaimer = (() => {
    if (deposit.type === 'EFT' && options.eftDisclaimer) {
      return (
        <Grid size={12}>
          <DisclaimerBox text={translateBackend(options.eftDisclaimer)} />
        </Grid>
      );
    }
    if (
      deposit.type === 'INTERNAL_TRANSFER' &&
      options.internalTransferDisclaimer
    ) {
      return (
        <Grid size={12}>
          <DisclaimerBox
            text={translateBackend(options.internalTransferDisclaimer)}
          />
        </Grid>
      );
    }
    if (
      deposit.type === 'EXTERNAL_TRANSFER' &&
      options.externalTransferDisclaimer
    ) {
      return (
        <Grid size={12}>
          <DisclaimerBox
            text={translateBackend(options.externalTransferDisclaimer)}
          />
        </Grid>
      );
    }
    return null;
  })();

  const depositDisplayTitle = t('depositReview.depositDisplayTitle', {
    amount: formatAmount(deposit.amountCents),
    account: getSubAccountName(detailedSubAccount as LegacyAccountTsType),
  });

  const onDeleteButtonClick: ComponentProps<typeof IconButton>['onClick'] =
    async (e) => {
      e.stopPropagation();
      const userConfirmation = await getUserConfirmation({
        title: t('depositReview.deleteDepositConfirmation.title', {
          depositDisplayTitle,
          amount: formatAmount(deposit.amountCents),
        }),
        bodyText: t('depositReview.deleteDepositConfirmation.bodyText', {
          depositDisplayTitle,
          amount: formatAmount(deposit.amountCents),
        }),
        setConfirmButtonColor: 'error',
      });
      if (!userConfirmation.confirmed) {
        userConfirmation.closeDialog();
        return;
      }
      if (deposit.temporaryId) {
        await onDepositDeleted(deposit.temporaryId);
      }
      userConfirmation.closeDialog();
    };

  const onEditButtonClick: ComponentProps<typeof IconButton>['onClick'] =
    async (e) => {
      e.stopPropagation();
      if (deposit.temporaryId) {
        await onDepositEdit(deposit.temporaryId);
      }
    };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      variant='workflow'
      state={
        { failed: 'error' as const, completed: undefined }[
          deposit.temporaryTransferState!
        ]
      }
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          borderTop: 'none !important',
          '.MuiAccordionSummary-content': {
            justifyContent: 'space-between !important',
          },
          '.MuiAccordionSummary-expandIconWrapper': {
            position: 'absolute',
            top: '10px',
            right: '15px',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {deposit.temporaryTransferState === 'completed' && (
                <CheckCircleIcon
                  sx={{ color: 'success.main', fontSize: '18px', mr: 1 }}
                />
              )}
              {deposit.temporaryTransferState === 'failed' && (
                <CancelIcon
                  sx={{ color: 'error.main', fontSize: '18px', mr: 1 }}
                />
              )}
              <Typography variant='bodyMedium' sx={{ fontWeight: 600 }}>
                {depositDisplayTitle}
              </Typography>
            </Box>
            <Box
              sx={{
                mr: 3,
              }}
            >
              {enableDelete &&
                deposit.temporaryTransferState !== 'completed' && (
                  <IconButton
                    aria-label='delete'
                    size='xsmall'
                    onClick={onDeleteButtonClick}
                  >
                    <DeleteIcon sx={{ fontSize: '15px' }} fontSize='inherit' />
                  </IconButton>
                )}
              {enableEdit && deposit.temporaryTransferState !== 'completed' && (
                <IconButton
                  aria-label='edit'
                  size='xsmall'
                  onClick={onEditButtonClick}
                >
                  <EditIcon sx={{ fontSize: '15px' }} fontSize='inherit' />
                </IconButton>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              mt: 1,
            }}
          >
            <Divider />
            {!expanded && (
              <Box
                sx={{
                  width: '100%',
                  mt: 2,
                  display: 'block',
                }}
              >
                <Grid
                  container
                  sx={{
                    rowGap: 2,
                  }}
                >
                  <FieldDisplay expanded={false} title={t('shared:type')}>
                    {getDepositTypeLabel(deposit.type)}
                  </FieldDisplay>
                  <FieldDisplay
                    expanded={false}
                    title={getDepositSourceLabel()}
                  >
                    {getDepositSource()}
                  </FieldDisplay>
                  {/* Disclaimer based on deposit type - shown in collapsed view */}
                  {disclaimer}
                </Grid>
              </Box>
            )}
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Grid
          container
          sx={{
            rowGap: 2,
          }}
        >
          <FieldDisplay title={t('shared:type')}>
            {getDepositTypeLabel(deposit.type)}
          </FieldDisplay>
          {deposit.type === 'EFT' && (
            <>
              <FieldDisplay title={t('depositReview.fromBankAccount')}>
                {`${deposit.bankAccount?.name} ${deposit.bankAccount?.bankAccountNumber}`}
              </FieldDisplay>
              <FieldDisplay title={t('depositReview.toSubAccount')}>
                {getSubAccountName(detailedSubAccount as LegacyAccountTsType)}
              </FieldDisplay>
              {deposit.holdings?.financialProduct && (
                <FieldDisplay title={t('depositReview.toHolding')}>
                  {translateBackend(
                    deposit.holdings.financialProduct.translatedName,
                  )}
                </FieldDisplay>
              )}
              <FieldDisplay title={t('depositReview.amount')}>
                {formatMoneyValue(deposit.amountCents, '$', 2, false)}
              </FieldDisplay>
              {deposit.schedule && deposit.schedule !== 'ONE_TIME' && (
                <>
                  <FieldDisplay title={t('depositReview.schedule')}>
                    {t(`schedule.${deposit.schedule}`)}
                  </FieldDisplay>
                  {deposit.scheduledDate && (
                    <FieldDisplay title={t('depositReview.startsOn')}>
                      {localizedDate(deposit.scheduledDate)}
                    </FieldDisplay>
                  )}
                </>
              )}
              {deposit.schedule === 'ONE_TIME' && deposit.scheduledDate && (
                <FieldDisplay title={t('depositReview.scheduledFor')}>
                  {localizedDate(deposit.scheduledDate)}
                </FieldDisplay>
              )}
            </>
          )}
          {deposit.type === 'INTERNAL_TRANSFER' && (
            <>
              <FieldDisplay title={t('depositReview.fromSubAccount')}>
                {getSubAccountName(
                  detailedFromSubAccount as LegacyAccountTsType,
                )}
              </FieldDisplay>
              <FieldDisplay title={t('depositReview.toSubAccount')}>
                {getSubAccountName(detailedSubAccount as LegacyAccountTsType)}
              </FieldDisplay>
              <FieldDisplay title={t('depositReview.amount')}>
                {fullTransfer()
                  ? t('depositReview.fullTransfer')
                  : formatMoneyValue(deposit.amountCents, '$', 2, false)}
              </FieldDisplay>
            </>
          )}
          {deposit.type === 'EXTERNAL_TRANSFER' && (
            <>
              <FieldDisplay title={t('depositReview.toSubAccount')}>
                {getSubAccountName(detailedSubAccount as LegacyAccountTsType)}
              </FieldDisplay>
              <FieldDisplay title={t('depositReview.financialInstitution')}>
                {deposit.institution?.name}
              </FieldDisplay>
              <FieldDisplay title={t('depositReview.accountNumber')}>
                {deposit.accountNumber}
              </FieldDisplay>
              <FieldDisplay title={t('depositReview.accountType')}>
                {deposit.accountType
                  ? t(`accountTypes:${deposit.accountType}`)
                  : null}
              </FieldDisplay>
              <FieldDisplay title={t('depositReview.transferType')}>
                {t(`depositReview.${deposit.transferType}`)}
              </FieldDisplay>
              <FieldDisplay title={t('depositReview.amount')}>
                {fullTransfer()
                  ? t('depositReview.fullTransfer')
                  : formatMoneyValue(deposit.amountCents, '$', 2, false)}
              </FieldDisplay>
            </>
          )}
          {/* Common fields for all types */}
          {deposit.fiscalYear && (
            <FieldDisplay title={t('components:fiscalYearTitle')}>
              {deposit.fiscalYear}
            </FieldDisplay>
          )}
          {/* Disclaimer based on deposit type */}
          {disclaimer}
        </Grid>
      </AccordionDetails>
      {confirmationDialog}
    </Accordion>
  );
};
